// chatBot.ts
import { openaiClient } from "../../services/openai";
import { getAssistantEvents, TABLE_ASSISTANT } from "./constants";
import {
    responseFormatKeys,
    responseFormatTemplate,
    responseValidationPrompt,
} from "./prompts";
import emitter from "src/helpers/emitter";
interface ChatMessage {
    role: "system" | "user" | "assistant";
    content: any;
    extra: MessageExtra; // remains an array
}

interface MessageExtra {
    userFacingMessage: string;
    backgroundMessage?: string; // optional
    data?: any; // optional
    validationResult?: { valid: boolean; error: string }; // optional
}

// Function to generate the base prompt
const generateBasePrompt = () => {
    return `Please provide your answer in the following JSON format:
{
  "data": ${responseFormatKeys.data},
  "text": "${responseFormatKeys.text}",
  "function":"${responseFormatKeys.function}",
  "process_text":"${responseFormatKeys.process_text}"
}

${responseFormatTemplate.validityNote}`;
};

export class ChatBot {
    private corePrompt: string;
    private model: string;
    private conversation: ChatMessage[];
    private assistantEvents: any;

    /**
     * Initializes the chatbot with a core prompt and model.
     * The core prompt is added as a system message to guide the conversation.
     *
     * @param corePrompt - The foundational instruction for the chatbot.
     * @param model - The OpenAI model to use (defaults to an environment variable).
     */
    constructor(
        corePrompt: string,
        model: string = process.env.OPENAI_MODEL || "gpt-4o-2024-08-06",
        userFacingPrompt: string = "",
    ) {
        this.corePrompt = corePrompt;
        this.model = model;
        // Start the conversation with the core prompt.
        this.conversation = [
            {
                role: "system",
                content: this.corePrompt,
                extra: {
                    userFacingMessage: userFacingPrompt,
                    backgroundMessage: this.corePrompt,
                },
            },
        ];
        this.assistantEvents = getAssistantEvents(TABLE_ASSISTANT);
    }

    /**
     * Adds a user message to the conversation.
     *
     * @param message - The user's message.
     */
    addUserMessage(message: string, extra: MessageExtra): any {
        const basePrompt = generateBasePrompt();
        const formattedPrompt = `${prompt}${basePrompt}`;

        const extraObj = {
            userFacingMessage: message,
            backgroundMessage: basePrompt,
            data: null,
        };

        const conversationObj = {
            role: "user",
            content: formattedPrompt,
            extra: extraObj,
        };
        // @ts-ignore
        this.conversation.push(conversationObj);
        return conversationObj;
    }

    /**
     * Adds an assistant message to the conversation.
     *
     * @param message - The assistant's message.
     */
    addAssistantMessage(message: string, extra: MessageExtra): any {
        const conversationObj: any = {
            role: "assistant",
            content: message,
            extra,
        };
        this.conversation.push(conversationObj);
        return conversationObj;
    }

    /**
     * Returns a formatted version of the conversation that only includes
     * userFacingMessage and backgroundMessage for each message.
     *
     * @returns An array of messages with just role and a combined content.
     */
    getFormattedConversation(): {
        role: "system" | "user" | "assistant";
        content: string;
    }[] {
        const formatted =
            this.conversation.length > 2
                ? this.conversation.map((message) => {
                      const userFacing = message.extra.userFacingMessage || "";
                      const background = message.extra.backgroundMessage || "";
                      const combinedContent = background
                          ? `${userFacing}\n${background}`
                          : userFacing;
                      return {
                          role: message.role,
                          content: combinedContent.trim(),
                      };
                  })
                : this.conversation.map((message) => {
                      const userFacing = message.extra.userFacingMessage || "";
                      const background = message.extra.backgroundMessage || "";
                      const combinedContent = background
                          ? `${userFacing}\n${background}`
                          : userFacing;
                      return {
                          role: message.role,
                          content: combinedContent.trim(),
                      };
                  });
        console.log("FormattedMessage", this.conversation, formatted);
        return formatted;
    }

    /**
     * Extracts and parses JSON from a string response with error handling
     * @param responseString - String that may contain JSON
     * @returns Parsed response object with default values if parsing fails
     */
    private extractAndParseJSON(responseString: string) {
        let parsedResponse = {
            data: null,
            text: "",
            function: "",
            process_text: "",
        };

        const jsonStart = responseString.indexOf("{");
        const jsonEnd = responseString.lastIndexOf("}");

        if (jsonStart !== -1 && jsonEnd !== -1) {
            const jsonString = responseString.substring(jsonStart, jsonEnd + 1);
            try {
                const parsed = JSON.parse(jsonString);
                parsedResponse = {
                    data: parsed.data,
                    text: parsed.text,
                    function: parsed.function,
                    process_text: parsed.process_text,
                };
            } catch (error) {
                console.error("Failed to parse JSON response:", error);
                parsedResponse = {
                    data: null,
                    text: responseString,
                    function: "",
                    process_text: "",
                };
            }
        } else {
            parsedResponse = {
                data: null,
                text: responseString,
                function: "",
                process_text: "",
            };
        }

        return parsedResponse;
    }

    /**
     * Extracts and parses validation JSON from a string response
     * @param validationString - String that may contain validation JSON
     * @returns Parsed validation object with default values if parsing fails
     */
    private parseValidationResponse(validationString: string) {
        let validationResult = {
            valid: false,
            error: "Failed to parse validation response",
        };

        const jsonStart = validationString.indexOf("{");
        const jsonEnd = validationString.lastIndexOf("}");

        if (jsonStart !== -1 && jsonEnd !== -1) {
            const jsonString = validationString.substring(
                jsonStart,
                jsonEnd + 1,
            );
            try {
                const parsed = JSON.parse(jsonString);
                validationResult = {
                    valid: Boolean(parsed.valid),
                    error: parsed.error || "",
                };
            } catch (error) {
                console.error("Failed to parse validation JSON:", error);
            }
        }

        return validationResult;
    }

    /**
     * Sends the formatted conversation to the OpenAI API and returns the assistant's response.
     * Will retry if validation fails, up to MAX_VALIDATION_RETRIES times.
     *
     * @returns The assistant's response as a string.
     */
    async getFormattedResponse(): Promise<string> {
        const formattedMessages = this.getFormattedConversation();
        const MAX_VALIDATION_RETRIES = 2; // Maximum number of retries when validation fails
        let currentAttempt = 0;
        let assistantResponse = "";
        let parsedResponse = null;
        let validationResult = { valid: false, error: "" };

        emitter.emit(this.assistantEvents.name, {
            message: this.assistantEvents?.events?.PROCESS_STARTED,
        });

        const generateResponse = async (insights: string) => {
            const response = await openaiClient.chat.completions.create({
                model: this.model,
                messages: insights
                    ? [
                          ...formattedMessages,
                          { role: "user", content: insights },
                      ]
                    : formattedMessages,
                temperature: 0.5,
            });
            return response;
        };

        // Retry loop until validation passes or max retries reached
        let prevInsights = "";
        while (
            currentAttempt < MAX_VALIDATION_RETRIES &&
            !validationResult.valid
        ) {
            currentAttempt++;
            console.log(
                `Attempt ${currentAttempt} of ${MAX_VALIDATION_RETRIES}`,
            );

            const response = await generateResponse(prevInsights);
            assistantResponse = response.choices[0].message.content ?? "";

            // Validate the response
            emitter.emit(this.assistantEvents.name, {
                message:
                    this.assistantEvents?.events?.PROCESS_RESPONSE_VALIDATION,
            });
            const responseValidation =
                await openaiClient.chat.completions.create({
                    model: this.model,
                    messages: [
                        {
                            role: "system",
                            content:
                                responseValidationPrompt +
                                `\n Previous conversation: ${JSON.stringify(
                                    formattedMessages[
                                        formattedMessages?.length - 1
                                    ],
                                )}`,
                        },
                        {
                            role: "assistant",
                            content: assistantResponse,
                        },
                    ],
                });

            const validationResponse =
                responseValidation.choices[0].message.content ?? "";

            // Parse responses using the appropriate methods
            parsedResponse = this.extractAndParseJSON(assistantResponse);
            validationResult = this.parseValidationResponse(validationResponse);

            if (validationResult.valid) {
                console.log(`Validation passed on attempt ${currentAttempt}`);
                emitter.emit(this.assistantEvents.name, {
                    message:
                        this.assistantEvents?.events
                            ?.PROCESS_RESPONSE_VALIDATION_SUCCESS,
                });
                break;
            } else {
                prevInsights = validationResult.error;
                console.warn(
                    `Attempt ${currentAttempt} validation failed: ${validationResult.error}`,
                );
                emitter.emit(this.assistantEvents.name, {
                    message:
                        this.assistantEvents?.events
                            ?.PROCESS_RESPONSE_VALIDATION_ERROR,
                });
                await new Promise((resolve) => setTimeout(resolve, 300));
                emitter.emit(this.assistantEvents.name, {
                    message:
                        this.assistantEvents?.events?.PROCESS_RESPONSE_RETRY,
                });
                // If we're about to hit the max retries, don't wait
                if (currentAttempt < MAX_VALIDATION_RETRIES) {
                    // Optional: Add exponential backoff or delay between retries
                    // await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }
        }

        // After retry loop ends, use the best response we have
        if (!validationResult.valid) {
            console.warn(
                `All ${MAX_VALIDATION_RETRIES} validation attempts failed. Using last response anyway.`,
            );
        }

        const conversationExtraObject = {
            userFacingMessage: parsedResponse?.text || "",
            backgroundMessage: parsedResponse?.process_text || "",
            data: parsedResponse,
            validationResult: validationResult,
        };

        return this.addAssistantMessage(
            assistantResponse,
            conversationExtraObject,
        );
    }
}
